'use client';

import { useCallback } from 'react';
import { Locale } from '@headless-workspace/config';
import { SideMenuModal } from '@headless-workspace/core-ui';
import { ProductTile } from '@headless-workspace/glow-ds';
import { ProductType } from '@headless-workspace/typings';
import type { VipBrandsValue } from '../../../server';
import { useAddToBasketMonoSkuBusinessRules } from './useAddToBasketMonoSkuBusinessRules';
import { useProductModalDisplay } from './useProductModalDisplay';

type UseProductTileActionsParams = {
    vipBrands: VipBrandsValue;
    locale: Locale;
    isNotifyMeEnabled: boolean;
    addIfAbsentAndOpen: (modalId: string, modal: SideMenuModal) => void;
};

export const useProductTileCTABusinessRules = ({
    vipBrands,
    locale,
    isNotifyMeEnabled,
}: UseProductTileActionsParams) => {
    const { handleAddToBasket, isAddingToBasket } = useAddToBasketMonoSkuBusinessRules();

    const { openQuickViewModal, openNotifyMeModal } = useProductModalDisplay();

    const handleProductModalView = useCallback(
        (isAvailable: boolean, product: ProductTile) => {
            if (!isAvailable && !isNotifyMeEnabled) {
                return () => null;
            }
            if (!isAvailable && isNotifyMeEnabled) {
                return () => openNotifyMeModal(product.sku, locale);
            }
            if (isAvailable && product.type !== ProductType.MONO_SKU) {
                return () => openQuickViewModal(product.sku, vipBrands, locale);
            }
            if (isAvailable && product.type === ProductType.MONO_SKU) {
                return () => handleAddToBasket(product);
            }
        },
        [isNotifyMeEnabled, openNotifyMeModal, locale, openQuickViewModal, vipBrands, handleAddToBasket],
    );

    return {
        handleProductModalView,
        isAddingToBasket,
    };
};
